import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import LazyLoadRoute from "../components/dashboard/LazyLoadRoute";
import RouteMap from "../components/dashboard/RouteMap";

const formatDate = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatStatus = (status) => {
  if (!status) return "-";
  return status
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function WaitlistOpening() {
  const { openingId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [opening, setOpening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [recPage, setRecPage] = useState(1);
  const [rejectedPage, setRejectedPage] = useState(1);
  const [showRejected, setShowRejected] = useState(false);
  const [showWaitlistedOnly, setShowWaitlistedOnly] = useState(false);
  const recsPerPage = 10;
  const rejectedPerPage = 10;
  const [recommendationRoutes, setRecommendationRoutes] = useState({});
  const [loadingRouteIds, setLoadingRouteIds] = useState(new Set());
  const [smsPreviewIds, setSmsPreviewIds] = useState(new Set());
  const [sendingOfferIds, setSendingOfferIds] = useState(new Set());

  useEffect(() => {
    const fetchOpening = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/schedule_openings/${openingId}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || "Failed to load opening");
        }
        setOpening(data);
        setRecPage(1);
        setRejectedPage(1);
        setShowRejected(false);
        setShowWaitlistedOnly(false);
        setRecommendationRoutes({});
        setLoadingRouteIds(new Set());
      } catch (err) {
        setError(err.message);
        setOpening(null);
      } finally {
        setLoading(false);
      }
    };

    if (openingId) {
      fetchOpening();
    }
  }, [openingId]);

  const refreshRecommendations = async () => {
    if (!openingId) return;
    setRefreshing(true);
    try {
      const response = await fetch(`/api/waitlist_openings/${openingId}/recommendations/refresh`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Failed to refresh recommendations");
      }
      const updatedResponse = await fetch(`/api/schedule_openings/${openingId}`);
      const updatedData = await updatedResponse.json();
      if (!updatedResponse.ok) {
        throw new Error(updatedData?.error || "Failed to reload opening");
      }
      setOpening(updatedData);
      setRecPage(1);
      setRejectedPage(1);
      setRecommendationRoutes({});
      setLoadingRouteIds(new Set());
      toast({
        title: "Recommendations refreshed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Refresh failed",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchRouteForRecommendation = async (recommendationId) => {
    if (loadingRouteIds.has(recommendationId) || recommendationRoutes[recommendationId]) {
      return;
    }
    setLoadingRouteIds((prev) => new Set(prev).add(recommendationId));
    try {
      const response = await fetch(`/api/schedule_opening_recommendations/${recommendationId}/calculate_route`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRecommendationRoutes((prev) => ({
          ...prev,
          [recommendationId]: data.data.routes,
        }));
      }
    } catch (err) {
      console.error(`Error fetching route for recommendation ${recommendationId}:`, err);
    } finally {
      setLoadingRouteIds((prev) => {
        const next = new Set(prev);
        next.delete(recommendationId);
        return next;
      });
    }
  };

  const handleToggleOpeningAutomation = async (nextOn) => {
    if (!opening?.id || automationSaving) return;

    const previousStatus = opening.waitlist_automation_status;
    const nextStatus = nextOn ? "on" : "off";

    setOpening((prev) => (prev ? { ...prev, waitlist_automation_status: nextStatus } : prev));
    setAutomationSaving(true);

    try {
      const response = await fetch(`/api/waitlist_openings/${opening.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ waitlist_automation_status: nextStatus }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Failed to update automation");
      }

      toast({
        title: `Automation ${nextOn ? "enabled" : "disabled"}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      setOpening((prev) => (prev ? { ...prev, waitlist_automation_status: previousStatus } : prev));
      toast({
        title: "Update failed",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setAutomationSaving(false);
    }
  };

  const toggleSmsPreview = (recommendationId) => {
    setSmsPreviewIds((prev) => {
      const next = new Set(prev);
      if (next.has(recommendationId)) {
        next.delete(recommendationId);
      } else {
        next.add(recommendationId);
      }
      return next;
    });
  };

  const handleSendOfferText = async (recommendationId) => {
    if (sendingOfferIds.has(recommendationId)) return;

    setSendingOfferIds((prev) => new Set(prev).add(recommendationId));
    try {
      const response = await fetch(`/api/schedule_opening_recommendations/${recommendationId}/send_offer_text`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Failed to send text");
      }

      const sentAt = data.data?.text_sent_at || new Date().toISOString();
      setOpening((prev) => {
        if (!prev) return prev;

        const recommendations = (prev.recommendations || []).map((rec) =>
          rec.id === recommendationId
            ? { ...rec, text_sent_at: sentAt, status: data.data?.status || "invite_sent_to_client_automatically" }
            : rec
        );

        return {
          ...prev,
          recommendations,
          texts_sent_count: recommendations.filter((rec) => rec.text_sent_at).length,
        };
      });
      toast({
        title: "Text sent",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Text not sent",
        description: err.message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSendingOfferIds((prev) => {
        const next = new Set(prev);
        next.delete(recommendationId);
        return next;
      });
    }
  };

  const isFilled = !!opening?.filled;
  const filledBy = opening?.filled_by;
  const recommendations = opening?.recommendations || [];
  const displayedRecommendations = showWaitlistedOnly
    ? recommendations.filter((rec) => rec.client?.waitlist_opted_in && rec.client?.waitlist_double_opted_in)
    : recommendations;
  const recTotalPages = Math.ceil(displayedRecommendations.length / recsPerPage);
  const rejectedItems = opening?.rejected_appointments || [];
  const rejectedTotalPages = Math.ceil(rejectedItems.length / rejectedPerPage);

  return (
    <Box p={{ base: 3, sm: 4, md: 8 }} bg="#F7F8FA" minH="100vh">
      <VStack align="stretch" spacing={6} maxW="1200px" mx="auto">
        <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} wrap="wrap" gap={3}>
          <Button variant="outline" onClick={() => navigate("/waitlist")} minH="44px">Back to Waitlist</Button>
          <HStack spacing={2} flexWrap="wrap">
            {opening?.id && (
              <Badge colorScheme="gray" variant="outline">Opening ID: {opening.id}</Badge>
            )}
            {!isFilled && (
              <Button
                variant="outline"
                onClick={refreshRecommendations}
                isLoading={refreshing}
                loadingText="Refreshing"
                minH="44px"
                flex={{ base: "1 1 100%", sm: "0 1 auto" }}
              >
                Refresh Recommendations
              </Button>
            )}
          </HStack>
        </Flex>

        <Heading size="md">{isFilled ? "Filled Calendar Opening" : "Calendar Opening"}</Heading>

        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="sm" color="blue.500" />
          </Flex>
        ) : error ? (
          <Box border="1px solid" borderColor="red.200" bg="red.50" p={4} borderRadius="md">
            <Text fontSize="sm" color="red.600">{error}</Text>
          </Box>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box border="1px solid" borderColor="gray.200" bg="gray.50" p={4} borderRadius="md">
                <Text fontSize="xs" color="gray.500" mb={2}>Previous Scheduled Item</Text>
                {opening?.previous_appointment ? (
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="600">{opening.previous_appointment.title}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatTime(opening.previous_appointment.start_at)} - {formatTime(opening.previous_appointment.end_at)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {opening.previous_appointment.service_location?.address || "-"}
                    </Text>
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">No previous item</Text>
                )}
              </Box>

              <Box border="1px solid" borderColor="blue.200" bg="blue.50" p={4} borderRadius="md">
                <Text fontSize="xs" color="gray.500" mb={2}>Opening Details</Text>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="600">{opening?.worker?.name || "-"}</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge variant="subtle" colorScheme="blue">{formatDate(opening?.start_time)}</Badge>
                    <Badge variant="subtle" colorScheme="gray">
                      {formatTime(opening?.start_time)} - {formatTime(opening?.end_time)}
                    </Badge>
                    <Badge variant="subtle" colorScheme="gray">{opening?.duration_minutes} min</Badge>
                  </HStack>
                  <HStack spacing={2}>
                    <Badge colorScheme={opening?.waitlist_automation_status === "on" ? "green" : "gray"}>
                      Automation {opening?.waitlist_automation_status === "on" ? "On" : "Off"}
                    </Badge>
                    <Badge colorScheme={opening?.filled ? "green" : "orange"}>
                      {opening?.filled ? "Filled" : "Open"}
                    </Badge>
                  </HStack>
                  <HStack spacing={2}>
                    <Switch
                      aria-label="Toggle opening automation"
                      colorScheme="green"
                      isChecked={opening?.waitlist_automation_status === "on"}
                      isDisabled={automationSaving || isFilled}
                      onChange={(e) => handleToggleOpeningAutomation(e.target.checked)}
                    />
                    <Text fontSize="sm" color="gray.600">
                      {isFilled
                        ? "Automation complete"
                        : automationSaving
                        ? "Saving..."
                        : `Automation ${opening?.waitlist_automation_status === "on" ? "On" : "Off"}`}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Box border="1px solid" borderColor="gray.200" bg="gray.50" p={4} borderRadius="md">
                <Text fontSize="xs" color="gray.500" mb={2}>Next Scheduled Item</Text>
                {opening?.next_appointment ? (
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="600">{opening.next_appointment.title}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatTime(opening.next_appointment.start_at)} - {formatTime(opening.next_appointment.end_at)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {opening.next_appointment.service_location?.address || "-"}
                    </Text>
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">No next item</Text>
                )}
              </Box>
            </SimpleGrid>

            {isFilled && filledBy && (
              <Box border="1px solid" borderColor="green.200" borderRadius="lg" p={6} bg="green.50">
                <Flex justify="space-between" align="start" gap={4} wrap="wrap">
                  <Box>
                    <Heading size="sm" color="green.900">Filled by {filledBy.client?.name || "waitlist customer"}</Heading>
                    <Text fontSize="sm" color="green.800" mt={1}>
                      {filledBy.job?.title || "Job"} moved into this opening{opening.filled_at ? ` on ${formatDateTime(opening.filled_at)}` : ""}.
                    </Text>
                  </Box>
                  <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                    Filled
                  </Badge>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={5}>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Customer</Text>
                    <Text fontWeight="600" color="gray.900">{filledBy.client?.name || "-"}</Text>
                    <Text fontSize="sm" color="gray.700">{filledBy.client_response_phone || filledBy.client?.phone_number || "-"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Original Appointment</Text>
                    <Text fontWeight="600" color="gray.900">{formatDateTime(filledBy.original_scheduled_time)}</Text>
                    <Text fontSize="sm" color="gray.700">
                      {filledBy.original_worker?.name ? `Original worker: ${filledBy.original_worker.name}` : "Original worker unavailable"}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Filled Opening</Text>
                    <Text fontWeight="600" color="gray.900">
                      {formatDateTime(filledBy.proposed_start_time)} - {formatTime(filledBy.proposed_end_time)}
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      {filledBy.new_worker?.name ? `New worker: ${filledBy.new_worker.name}` : `Worker: ${opening.worker?.name || "-"}`}
                    </Text>
                  </Box>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={5}>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Job</Text>
                    <Text fontSize="sm" color="gray.800">{filledBy.job?.title || "-"}</Text>
                    <Text fontSize="sm" color="gray.600">{filledBy.job?.duration_minutes || "-"} min</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Address</Text>
                    <Text fontSize="sm" color="gray.800">{filledBy.address || "-"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="green.700" mb={1}>Accepted</Text>
                    <Text fontSize="sm" color="gray.800">{formatDateTime(filledBy.client_responded_at)}</Text>
                    <Text fontSize="sm" color="gray.600">{formatStatus(filledBy.status)}</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            )}

            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="white">
                <StatLabel fontSize="xs" color="gray.500">Recommendations</StatLabel>
                <StatNumber fontSize="lg">{opening?.recommendations_count ?? "-"}</StatNumber>
              </Stat>
              <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="white">
                <StatLabel fontSize="xs" color="gray.500">Texts Sent</StatLabel>
                <StatNumber fontSize="lg">{opening?.texts_sent_count ?? "-"}</StatNumber>
              </Stat>
              <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="white">
                <StatLabel fontSize="xs" color="gray.500">Status Summary</StatLabel>
                <StatNumber fontSize="lg">{opening?.status_summary || "-"}</StatNumber>
              </Stat>
              <Stat border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="white">
                <StatLabel fontSize="xs" color="gray.500">Day of Week</StatLabel>
                <StatNumber fontSize="lg">{opening?.day_of_week || "-"}</StatNumber>
              </Stat>
            </SimpleGrid>

            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 6 }} bg="white">
              <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
                <Heading size="sm">{showRejected ? "Rejected Scheduled Items" : "Recommendations"}</Heading>
                <HStack spacing={2} flexWrap="wrap" w={{ base: "full", sm: "auto" }} sx={{ "& > button": { minHeight: "44px", width: { base: "100%", sm: "auto" } } }}>
                  {!showRejected && (
                    <Button
                      size="sm"
                      variant={showWaitlistedOnly ? "solid" : "outline"}
                      colorScheme={showWaitlistedOnly ? "blue" : "gray"}
                      onClick={() => {
                        setShowWaitlistedOnly((prev) => !prev);
                        setRecPage(1);
                      }}
                    >
                      {showWaitlistedOnly ? "Show All Recommendations" : "Show Waitlist Confirmed Only"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowRejected((prev) => !prev);
                      setRecPage(1);
                      setRejectedPage(1);
                    }}
                  >
                    {showRejected ? "Show Recommendations" : `Show Rejected (${rejectedItems.length})`}
                  </Button>
                </HStack>
              </Flex>
              <Divider mb={4} />
              {!showRejected && displayedRecommendations.length > 0 ? (
                <VStack align="stretch" spacing={4}>
                  {displayedRecommendations
                    .slice((recPage - 1) * recsPerPage, recPage * recsPerPage)
                    .map((rec) => {
                      const sendDisabled = isFilled || sendingOfferIds.has(rec.id) || !!rec.text_sent_at || !!rec.client?.waitlist_texting_blocked_reason;

                      return (
                    <LazyLoadRoute
                      key={rec.id}
                      recommendationId={rec.id}
                      onVisible={fetchRouteForRecommendation}
                    >
                      <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={{ base: 3, sm: 4 }} minW={0}>
                        <Flex justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} mb={3} wrap="wrap" gap={2}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Badge variant="outline">#{rec.rank}</Badge>
                            <Text fontWeight="600">{rec.client?.name || "-"}</Text>
                            {rec.client?.phone_number && (
                              <Text fontSize="sm" color="gray.600">{rec.client.phone_number}</Text>
                            )}
                            <Text fontSize="sm" color="gray.500">{rec.job?.title || "-"}</Text>
                          </HStack>
                          <HStack spacing={2} flexWrap="wrap">
                            {rec.text_sent_at && (
                              <Badge colorScheme="green">Text sent {formatDateTime(rec.text_sent_at)}</Badge>
                            )}
                            {rec.spot_taken_text_sent_at && (
                              <Badge colorScheme="orange">Spot taken follow-up sent {formatDateTime(rec.spot_taken_text_sent_at)}</Badge>
                            )}
                            {rec.client_responded_at && (
                              <Badge colorScheme="purple">Accepted at {formatDateTime(rec.client_responded_at)}</Badge>
                            )}
                            <Badge colorScheme="gray">{formatStatus(rec.status)}</Badge>
                          </HStack>
                        </Flex>

                        <HStack spacing={3} mb={4} flexWrap="wrap" sx={{ "& > a, & > button": { minHeight: "44px", width: { base: "100%", sm: "auto" } } }}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => handleSendOfferText(rec.id)}
                            isLoading={sendingOfferIds.has(rec.id)}
                            loadingText="Queued"
                            isDisabled={sendDisabled}
                          >
                            {isFilled ? "Filled" : sendingOfferIds.has(rec.id) ? "Queued" : rec.text_sent_at ? "Text Sent" : "Send Text"}
                          </Button>
                          <Button
                            as="a"
                            href={rec.offer_preview_url}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            variant="outline"
                          >
                            Preview Client Page
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSmsPreview(rec.id)}
                          >
                            {smsPreviewIds.has(rec.id) ? "Hide Text Preview" : "Preview Text"}
                          </Button>
                        </HStack>

                        {rec.client?.waitlist_texting_blocked_reason && (
                          <Box mb={4} border="1px solid" borderColor="orange.200" bg="orange.50" borderRadius="md" p={4}>
                            <Text fontSize="sm" color="orange.900" fontWeight="semibold">
                              Automatic texting is unavailable
                            </Text>
                            <Text fontSize="sm" color="orange.800" mt={1}>
                              {rec.client.waitlist_texting_blocked_reason} You can call them at {rec.client.phone_number || "the phone number on file"}.
                            </Text>
                          </Box>
                        )}

                        {smsPreviewIds.has(rec.id) && (
                          <Box mb={4} border="1px solid" borderColor="blue.100" bg="blue.50" borderRadius="md" p={4}>
                            <Text fontSize="xs" color="blue.700" fontWeight="semibold" mb={2}>
                              SMS Preview
                            </Text>
                            <Text fontSize="sm" color="gray.800">
                              {rec.sms_preview_text}
                            </Text>
                          </Box>
                        )}

                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} fontSize="sm" color="gray.600">
                          <Box>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} fontSize="sm" color="gray.600">
                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Proposed Time</Text>
                                <Text>{formatDateTime(rec.proposed_start_time)} - {formatDateTime(rec.proposed_end_time)}</Text>
                                <Text fontSize="xs" color="gray.500" mt={3} mb={1}>Address</Text>
                                <Text>{rec.address || "-"}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Original Time</Text>
                                <Text>{formatDateTime(rec.original_scheduled_time)}</Text>
                              </Box>
                              <Box />
                            </SimpleGrid>
                          </Box>

                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={2}>Route</Text>
                            {recommendationRoutes[rec.id] ? (
                              <VStack align="stretch" spacing={2}>
                                {recommendationRoutes[rec.id].a_to_b && (
                                  <Text fontSize="sm" color="gray.600">
                                    A → B: {(recommendationRoutes[rec.id].a_to_b.distance_km * 0.621371).toFixed(1)} mi
                                    {" "}({Math.ceil(recommendationRoutes[rec.id].a_to_b.duration_minutes)} min)
                                  </Text>
                                )}
                                {recommendationRoutes[rec.id].b_to_c && (
                                  <Text fontSize="sm" color="gray.600">
                                    B → C: {(recommendationRoutes[rec.id].b_to_c.distance_km * 0.621371).toFixed(1)} mi
                                    {" "}({Math.ceil(recommendationRoutes[rec.id].b_to_c.duration_minutes)} min)
                                  </Text>
                                )}
                                <RouteMap routes={recommendationRoutes[rec.id]} height="160px" />
                              </VStack>
                            ) : (
                              <Box>
                                {loadingRouteIds.has(rec.id) ? (
                                  <Text fontSize="sm" color="gray.500">Calculating route...</Text>
                                ) : rec.estimated_drive_time ? (
                                  <Text fontSize="sm" color="gray.500">
                                    Estimated drive time: {Math.ceil(rec.estimated_drive_time)} min
                                  </Text>
                                ) : (
                                  <Text fontSize="sm" color="gray.500">No route data yet.</Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        </SimpleGrid>
                      </Box>
                    </LazyLoadRoute>
                      );
                    })}
                </VStack>
              ) : !showRejected ? (
                <Text fontSize="sm" color="gray.500">
                  {showWaitlistedOnly ? "No waitlisted recommendations for this opening yet." : "No recommendations for this opening yet."}
                </Text>
              ) : rejectedItems.length > 0 ? (
                <VStack align="stretch" spacing={4}>
                  {rejectedItems
                    .slice((rejectedPage - 1) * rejectedPerPage, rejectedPage * rejectedPerPage)
                    .map((item) => (
                      <Box key={item.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                        <Flex justify="space-between" align="start" mb={3} wrap="wrap" gap={3}>
                          <Box>
                            <Text fontWeight="600">{item.client?.name || "Unknown client"}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {item.job?.title || item.appointment?.title || "Appointment"}
                            </Text>
                          </Box>
                          <Badge colorScheme="red">{formatStatus(item.rejection_reason)}</Badge>
                        </Flex>

                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} fontSize="sm" color="gray.600">
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Original Time</Text>
                            <Text>{formatDateTime(item.appointment?.start_at)} - {formatTime(item.appointment?.end_at)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Address</Text>
                            <Text>{item.address || "-"}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Rejected Because</Text>
                            <Text>{item.rejection_message}</Text>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">No rejected scheduled items for this opening.</Text>
              )}

              {!showRejected && displayedRecommendations.length > recsPerPage && (
                <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRecPage((prev) => Math.max(1, prev - 1))}
                    isDisabled={recPage <= 1}
                  >
                    Previous
                  </Button>

                  <HStack spacing={1} flexWrap="wrap">
                    {Array.from({
                      length: recTotalPages,
                    })
                      .map((_, index) => index + 1)
                      .filter((page) =>
                        page === 1 ||
                        page === recTotalPages ||
                        (page >= recPage - 2 && page <= recPage + 2)
                      )
                      .map((page) => {
                        const isActive = page === recPage;
                        return (
                          <Button
                            key={page}
                            size="sm"
                            variant={isActive ? "solid" : "outline"}
                            colorScheme={isActive ? "blue" : "gray"}
                            onClick={() => setRecPage(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                  </HStack>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setRecPage((prev) =>
                        Math.min(recTotalPages, prev + 1)
                      )
                    }
                    isDisabled={recPage >= recTotalPages}
                  >
                    Next
                  </Button>
                </Flex>
              )}

              {showRejected && rejectedItems.length > rejectedPerPage && (
                <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectedPage((prev) => Math.max(1, prev - 1))}
                    isDisabled={rejectedPage <= 1}
                  >
                    Previous
                  </Button>

                  <HStack spacing={1} flexWrap="wrap">
                    {Array.from({ length: rejectedTotalPages })
                      .map((_, index) => index + 1)
                      .filter((page) =>
                        page === 1 ||
                        page === rejectedTotalPages ||
                        (page >= rejectedPage - 2 && page <= rejectedPage + 2)
                      )
                      .map((page) => {
                        const isActive = page === rejectedPage;
                        return (
                          <Button
                            key={page}
                            size="sm"
                            variant={isActive ? "solid" : "outline"}
                            colorScheme={isActive ? "blue" : "gray"}
                            onClick={() => setRejectedPage(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                  </HStack>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectedPage((prev) => Math.min(rejectedTotalPages, prev + 1))}
                    isDisabled={rejectedPage >= rejectedTotalPages}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
}
